import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton, Tab, Tabs, TextField } from '@mui/material';
import { Button } from '@mui/material';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import AddAndEditJobV2 from '../../../common/AddAndEditJobV2';
import TeamSelectMenu from '../../../common/components/TeamSelectMenu';
import SortMenu from './SortMenu';

interface JobQueueHeaderProps {
  mainTab: number;
}

const JobQueueHeader: React.FC<JobQueueHeaderProps> = ({ mainTab }) => {
  const [openAddJob, setOpenAddJob] = useState(false);
  const { managementJobQueueService } = useStore();
  const { selectedSort, selectedTeamName, selectedTeam } =
    managementJobQueueService;
  const [localSearch, setLocalSearch] = useState(
    managementJobQueueService.searchTerm,
  );
  const debouncedSearch = useDebounce(localSearch, 500);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [localPercentage, setLocalPercentage] = useState(
    managementJobQueueService.percentage,
  );
  const debouncedPercentage = useDebounce(localPercentage, 500);

  // Sync debounced search to MobX store
  useEffect(() => {
    managementJobQueueService.setSearchTerm(debouncedSearch);
  }, [debouncedSearch, managementJobQueueService]);

  // Sync debounced percentage to MobX store
  useEffect(() => {
    managementJobQueueService.setPercentage(debouncedPercentage);
  }, [debouncedPercentage, managementJobQueueService]);

  const handleTeamClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleTeamClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleTeamSelect = useCallback(
    (team: string, teamName: string) => {
      managementJobQueueService.setSelectedTeam(team);
      managementJobQueueService.setSelectedTeamName(teamName);
      setAnchorEl(null);
    },
    [managementJobQueueService],
  );

  const handleSortClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSortAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleSortClose = useCallback(() => {
    setSortAnchorEl(null);
  }, []);

  const handleSortSelect = useCallback(
    (sort: string) => {
      managementJobQueueService.setSelectedSort(sort);
      setSortAnchorEl(null);
    },
    [managementJobQueueService],
  );

  const handleMainTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managementJobQueueService.setMainTab(newValue);
    },
    [managementJobQueueService],
  );

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handlePercentageChange = useCallback((value: string) => {
    setLocalPercentage(value);
  }, []);

  return (
    <Box className="main-tabs-container">
      <Tabs
        value={mainTab}
        onChange={handleMainTabChange}
        className="main-tabs"
        TabIndicatorProps={{
          className: 'tab-indicator',
        }}>
        <Tab label="Bench Queue" className="main-tab" />
        <Tab label="Job Queue" className="main-tab" />
      </Tabs>

      <Box className="header-actions">
        <TextField
          placeholder="Search"
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="search-field"
          size="small"
        />
        <Box className="team-select" onClick={handleTeamClick}>
          {selectedTeamName || 'All Teams'}
          <KeyboardArrowDownIcon sx={{ color: '#242424', fontSize: 20 }} />
        </Box>
        <TeamSelectMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleTeamClose}
          selectedTeam={selectedTeam}
          onSelect={handleTeamSelect}
        />
        <IconButton className="sort-button" onClick={handleSortClick}>
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <SortMenu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          selectedSort={selectedSort}
          onSelect={handleSortSelect}
        />
        <Box className="percentage-input-container">
          <TextField
            value={localPercentage}
            onChange={(e) => handlePercentageChange(e.target.value)}
            className="percentage-input"
            placeholder="0"
            size="small"
            InputProps={{
              endAdornment: (
                <Box
                  component="span"
                  sx={{ color: '#5E6C84', fontSize: '13px' }}>
                  %
                </Box>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenAddJob(true)}
          sx={{
            '&:hover': { bgcolor: '#374151', boxShadow: 'none' },
            bgcolor: '#111827',
            borderRadius: '4px',
            boxShadow: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            height: '32px',
            minWidth: '90px',
            ml: 2,
            textTransform: 'none',
          }}>
          + Add Job
        </Button>
      </Box>
      <AddAndEditJobV2
        open={openAddJob}
        onClose={() => setOpenAddJob(false)}
        scroll="paper"
        isBenchQueue={mainTab === 0}
      />
    </Box>
  );
};

export default observer(JobQueueHeader);
